import React from "react";
import { Box, Typography, Checkbox, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HPTag, { HPTagColor } from "@components/common/Tag";
import Tooltip from "@components/common/Tooltip";
import { User } from "@/hooks/use-share-modal-logic";

interface UserRowProps {
  user: User;
  onSelect: (email: string) => void;
  isSelected: boolean;
  onRevoke?: (email: string) => void;
  isShared: boolean;
}

const getUserStatusConfig = (status: string) => {
  switch (status?.toLowerCase()) {
    case "verified":
      return {
        color: HPTagColor.GREEN,
        text: "Verified",
      };
    case "unverified":
      return {
        color: HPTagColor.GRAY,
        text: "Unverified",
      };
    case "locked":
      return {
        color: HPTagColor.RED,
        text: "Locked",
      };
    default:
      return {
        color: HPTagColor.GRAY,
        text: status || "Unknown",
      };
  }
};

const UserRow: React.FC<UserRowProps> = ({
  user,
  onSelect,
  isSelected,
  onRevoke,
  isShared,
}) => {
  const statusConfig = getUserStatusConfig(user.status);

  return (
    <Box
      display="flex"
      alignItems="center"
      py={1}
      px={2}
      className="hover:bg-[#F7F7F8]"
    >
      <Box
        sx={{
          width: "42px",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Checkbox
          checked={isSelected}
          onChange={() => onSelect(user.email)}
          size="small"
        />
      </Box>

      <Box sx={{ flex: 1.2, pr: 2, minWidth: 0 }}>
        <Tooltip title={user.name} placement="top" arrow>
          <Typography variant="body2" fontWeight="medium" noWrap>
            {user.name}
          </Typography>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 2.5, pr: 2, minWidth: 0 }}>
        <Tooltip title={user.email} placement="top" arrow>
          <Typography
            variant="body2"
            color="textSecondary"
            noWrap
            sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {user.email}
          </Typography>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, pr: 2, minWidth: 0 }}>
        <HPTag
          color={statusConfig.color}
          ariaLabel={`User status: ${statusConfig.text}`}
          hasBorder={false}
        >
          {statusConfig.text}
        </HPTag>
      </Box>

      <Box sx={{ flex: 1.1, pr: 2, minWidth: 0 }}>
        <Typography variant="body2" noWrap>
          {isShared ? "Assigned" : "Not Assigned"}
        </Typography>
      </Box>

      <Box sx={{ flex: 1, pr: 2, minWidth: 0 }}>
        <Tooltip title={user.company || "-"} placement="top" arrow>
          <Typography variant="body2" noWrap>
            {user.company || "-"}
          </Typography>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, pr: 2, minWidth: 0 }}>
        <Typography variant="body2" noWrap>
          {user.lastAccessed || "-"}
        </Typography>
      </Box>

      <Box sx={{ width: 36, flexShrink: 0, ml: 1 }}>
        {isShared && onRevoke ? (
          <Tooltip title="Revoke access" placement="top" arrow>
            <IconButton
              size="small"
              onClick={() => onRevoke(user.email)}
              aria-label="Revoke access"
              sx={{
                ml: 1,
                color: "text.secondary",
                "&:hover": {
                  color: "error.main",
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Box sx={{ width: 36, ml: 1 }} />
        )}
      </Box>
    </Box>
  );
};

export default UserRow;
